/**
 * Transcription
 *
 * Interactive transcript display for AI transcription segments.
 *
 * Primary responsibilities:
 * - Render transcript segments through a render-props API
 * - Highlight active, past, and future transcript segments
 * - Seek media playback when users click transcript text
 *
 * Dependencies:
 * - components/ai-elements/use-controllable-state
 */

'use client'

import {
  createContext,
  type ComponentProps,
  type ReactNode,
  useContext,
  useMemo
} from 'react'

import { useControllableState } from '@/components/ai-elements/use-controllable-state'
import { cn } from '@/lib/utils'
import type { ChatVoiceMemoTranscriptSegment } from '@/types/voice-memo'

interface TranscriptionContextValue {
  currentTime: number
  onSeek?: (time: number) => void
  setCurrentTime: (time: number) => void
}

const TranscriptionContext = createContext<TranscriptionContextValue | null>(null)

// --------------

function useTranscriptionContext() {
  const context = useContext(TranscriptionContext)

  if (!context) {
    throw new Error('TranscriptionSegment must be used within Transcription')
  }

  return context
}

export interface TranscriptionProps
  extends Omit<ComponentProps<'div'>, 'children'> {
  children: (segment: ChatVoiceMemoTranscriptSegment, index: number) => ReactNode
  currentTime?: number
  onSeek?: (time: number) => void
  segments: ChatVoiceMemoTranscriptSegment[]
}

export function Transcription({
  children,
  className,
  currentTime,
  onSeek,
  segments,
  ...props
}: TranscriptionProps) {
  const [activeTime, setActiveTime] = useControllableState({
    prop: currentTime,
    defaultProp: 0,
    onChange: onSeek
  })
  const filteredSegments = useMemo(
    () => segments.filter((segment) => segment.text.trim().length > 0),
    [segments]
  )
  const contextValue = useMemo(
    () => ({
      currentTime: activeTime,
      onSeek,
      setCurrentTime: setActiveTime
    }),
    [activeTime, onSeek, setActiveTime]
  )

  return (
    <TranscriptionContext.Provider value={contextValue}>
      <div
        className={cn('flex flex-wrap gap-1 text-sm leading-relaxed', className)}
        data-slot="transcription"
        {...props}
      >
        {filteredSegments.map((segment, index) => children(segment, index))}
      </div>
    </TranscriptionContext.Provider>
  )
}

export interface TranscriptionSegmentProps extends ComponentProps<'button'> {
  index: number
  segment: ChatVoiceMemoTranscriptSegment
}

export function TranscriptionSegment({
  className,
  index,
  segment,
  onClick,
  ...props
}: TranscriptionSegmentProps) {
  const { currentTime, onSeek, setCurrentTime } = useTranscriptionContext()
  const isActive =
    currentTime >= segment.startSecond && currentTime <= segment.endSecond
  const isPast = currentTime > segment.endSecond
  const isInteractive = Boolean(onSeek)

  return (
    <button
      className={cn(
        'rounded-sm text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'text-primary'
          : isPast
            ? 'text-muted-foreground'
            : 'text-muted-foreground/60',
        isInteractive ? 'cursor-pointer hover:text-foreground' : 'cursor-default',
        className
      )}
      data-active={isActive ? '' : undefined}
      data-index={index}
      data-slot="transcription-segment"
      disabled={!isInteractive}
      type="button"
      onClick={(event) => {
        setCurrentTime(segment.startSecond)
        onClick?.(event)
      }}
      {...props}
    >
      {segment.text}
    </button>
  )
}
